import { useState } from 'react';
import {
  Actions,
  AppArea,
  IssueTicketListFilterInput,
  IssueTicketListSortingInput,
  IssueTicketStatus,
  IssueTicketsDocument,
  SortingDirection,
  Subjects,
} from 'src/graphql/generated/graphql';
import { useTable } from 'src/hooks/use-table';
import { IssueTicketsFilter } from './filter';
import { Button, Tabs } from '@otp/ui';
import { IssueTicketsTable } from './table';
import { useTranslation } from 'react-i18next';
import { MUISortOptions } from 'mui-datatables';
import { IssueTicketForm } from '../form';
import { IssueTicketDetails } from '../details/details';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import { Can } from 'src/utils/can';
import { useIssueTickets } from 'src/hooks/query/use-issue-tickets';

const PAGINATION_LIMIT = 20;

enum TabType {
  Open = 'open',
  Resolved = 'resolved',
}

export const IssueTicketsList = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'issue.ticket.list',
  });

  const [activeTab, setActiveTab] = useState(TabType.Open);
  const [displayIssueTicketForm, setDisplayIssueTicketForm] = useState(false);
  const [selectedIssueTicketId, setSelectedIssueTicketId] = useState<
    string | null
  >(null);

  const tabs = [
    { label: t('tabs.open'), value: TabType.Open },
    { label: t('tabs.resolved'), value: TabType.Resolved },
  ];

  const getFilterStatus = (activeTab: TabType) => {
    switch (activeTab) {
      case TabType.Open:
        return [
          IssueTicketStatus.Open,
          IssueTicketStatus.InProgress,
          IssueTicketStatus.Blocked,
        ];
      default:
        return [IssueTicketStatus.Resolved];
    }
  };

  const {
    page,
    setPage,
    filter,
    onFilterChange,
    defaultFilter,
    sorting,
    sortingField,
    sortingDirection,
    onSortChange,
  } = useTable<IssueTicketListFilterInput, Record<string, SortingDirection>>({
    defaultSorting: {
      createdAt: SortingDirection.Desc,
    },
  });

  const sortOrder: MUISortOptions | undefined = sortingField
    ? {
        name: sortingField,
        direction: sortingDirection === SortingDirection.Desc ? 'desc' : 'asc',
      }
    : undefined;

  const { issueTickets, loading, totalCount } = useIssueTickets({
    filter: {
      ...filter,
      status: getFilterStatus(activeTab),
    },
    sorting: sorting as IssueTicketListSortingInput,
    pagination: {
      limit: PAGINATION_LIMIT,
      offset: page * PAGINATION_LIMIT,
    },
  });

  const onRowClick = (_: any, { rowIndex }: { rowIndex: number }) => {
    setSelectedIssueTicketId(issueTickets[rowIndex].id);
  };

  const onNewIssueTicketSaved = () => {
    setDisplayIssueTicketForm(false);
  };

  return (
    <Can I={Actions.Read} a={Subjects.Issues} appArea={AppArea.Common}>
      <FilterWrapper>
        <IssueTicketsFilter
          onSubmit={onFilterChange}
          defaultFilter={defaultFilter}
        />
      </FilterWrapper>

      <ContentWrapper>
        <TableWrapper>
          <Tabs
            className="mb-2"
            tabs={tabs}
            onChange={(_, value) => setActiveTab(value)}
            value={activeTab}
            extra={
              <Can
                I={Actions.Write}
                a={Subjects.Issues}
                appArea={AppArea.Common}
              >
                <Button
                  variant="contained"
                  onClick={() => setDisplayIssueTicketForm(true)}
                >
                  {t('tabs.create-issue')}
                </Button>
              </Can>
            }
          />

          <IssueTicketsTable
            data={issueTickets}
            loading={loading}
            options={{
              page,
              count: totalCount,
              sort: true,
              onColumnSortChange: onSortChange,
              sortOrder,
              rowsPerPage: PAGINATION_LIMIT,
              onRowClick,
              onChangePage: setPage,
              enableNestedDataAccess: '.',
            }}
          />
        </TableWrapper>
      </ContentWrapper>

      {displayIssueTicketForm && (
        <IssueTicketForm
          refetchQueries={[IssueTicketsDocument]}
          onCancel={() => setDisplayIssueTicketForm(false)}
          onSave={onNewIssueTicketSaved}
        />
      )}
      {selectedIssueTicketId && (
        <IssueTicketDetails
          issueTicketId={selectedIssueTicketId}
          refetchQueries={[IssueTicketsDocument]}
          onClose={() => setSelectedIssueTicketId(null)}
        />
      )}
    </Can>
  );
};
